/**
 * ショートカットのウィジェット。
 *
 * サムネイルをタップすると作品を直接開く。右下のバッジから全件のシートを出し、
 * そこで並びの確認と削除を行う。
 */

import { Bookmark, MoreHorizontal, Trash2 } from 'lucide-react'
import { useCallback, useState } from 'react'

import { getProductById } from '@/api/api'
import type { ShortcutItem } from '@/api/services/product'
import type { NavigateFn } from '@/components/home/home-widget-data'
import { ThumbnailGridWidget } from '@/components/home/thumbnail-grid-widget'
import { AuthImage } from '@/components/ui/auth-image'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import type { WidgetSize } from '@/store/useHomeLayoutStore'
import { useShortcutStore } from '@/store/useShortcutStore'
import { NON_READABLE_MIME_TYPES } from '@/types/ebook-conversion'

const thumbnailSrc = (productId: number): string => `/api/p/${productId}/thumbnail`

const cacheKeyOf = (productId: number): string => `shortcut-${productId}`

const Thumbnail = ({ item, className }: { item: ShortcutItem; className?: string }) => (
  <AuthImage
    src={thumbnailSrc(item.product_id)}
    alt={item.title}
    className={className ?? 'size-full object-cover'}
    cacheKey={cacheKeyOf(item.product_id)}
  />
)

/**
 * 全件シートを開くバッジ。入りきらない件数があれば +N、無ければ「…」を出す。
 * 削除はシートからしかできないので、溢れていなくても必ず出す。
 * button の入れ子になる場所があるため span で受ける。
 */
const MoreBadge = ({ overflow, onOpen }: { overflow: number; onOpen: () => void }) => (
  <span
    role="button"
    tabIndex={-1}
    aria-label={overflow > 0 ? `他 ${overflow} 件` : 'ショートカット一覧'}
    onClick={(event) => {
      event.stopPropagation()
      onOpen()
    }}
    className="absolute right-1 bottom-1 flex items-center justify-center rounded-full bg-black/70 px-1.5 py-0.5 text-[10px] leading-none font-semibold text-white tabular-nums"
  >
    {overflow > 0 ? `+${overflow}` : <MoreHorizontal size={11} />}
  </span>
)

export const ShortcutWidget = ({ size, onNavigate }: { size: WidgetSize; onNavigate: NavigateFn }) => {
  const shortcuts = useShortcutStore((state) => state.shortcuts)
  const removeProductShortcut = useShortcutStore((state) => state.removeProductShortcut)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const openSheet = useCallback(() => setIsSheetOpen(true), [])

  const openShortcut = useCallback(
    async (productId: number, domain: string) => {
      try {
        const product = await getProductById(productId)
        if (!product) return

        if (domain === 'audiobook') {
          const audioEpisode = product.episodes?.find((ep) => ep.mime_type === 'audio/mpeg')
          if (audioEpisode) {
            onNavigate(`/p/${audioEpisode.episode_id}`)
            return
          }
        }

        const firstEpisode = product.episodes?.[0]
        const shouldOpenList =
          (product.episodes && product.episodes.length >= 2) ||
          (firstEpisode?.mime_type && NON_READABLE_MIME_TYPES.has(firstEpisode.mime_type))

        if (shouldOpenList) {
          onNavigate(`/p?domain=${domain}&openEpisodes=${productId}`)
          return
        }

        if (firstEpisode) {
          onNavigate(`/p/${firstEpisode.episode_id}`)
        }
      } catch {
        onNavigate(`/p?domain=${domain}`)
      }
    },
    [onNavigate],
  )

  const sheet = (
    <BottomSheet title="ショートカット" open={isSheetOpen} onOpenChange={setIsSheetOpen} snapPoints={[0.92]}>
      <ul className="flex flex-col gap-1 px-4 pb-4">
        {shortcuts.map((item) => (
          <li key={item.product_id} className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setIsSheetOpen(false)
                void openShortcut(item.product_id, item.domain)
              }}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-lg border-none bg-transparent p-2 text-left active:opacity-70"
            >
              <div className="size-12 shrink-0 overflow-hidden rounded-md bg-muted">
                <Thumbnail item={item} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{item.title}</div>
                <div className="truncate text-[11px] text-muted-foreground">{item.domain}</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => void removeProductShortcut(item.product_id)}
              aria-label={`${item.title} をショートカットから削除`}
              className="shrink-0 rounded-md border-none bg-transparent p-2 text-muted-foreground active:opacity-70"
            >
              <Trash2 size={16} />
            </button>
          </li>
        ))}
      </ul>
    </BottomSheet>
  )

  return (
    <>
      <ThumbnailGridWidget
        items={shortcuts.map((item) => ({
          key: item.product_id,
          title: item.title,
          thumbnail: <Thumbnail item={item} />,
          onOpen: () => void openShortcut(item.product_id, item.domain),
        }))}
        size={size}
        icon={Bookmark}
        label="ショートカット"
        accentClassName="text-violet-400"
        emptyMessage="ショートカットなし"
        onOpenEmpty={openSheet}
        renderOverlay={(overflow) => shortcuts.length > 0 && <MoreBadge overflow={overflow} onOpen={openSheet} />}
      />
      {sheet}
    </>
  )
}
